import { THEMES, WORLD_H, WORLD_W, type ThemeId } from '@cuboom/shared';
import { Camera } from './camera.js';
import { drawBackdrop } from './backdrop.js';
import type { TerrainLayer } from './terrainLayer.js';

/**
 * Canvas stack + main loop for the debug terrain scene.
 * Phase 4+ adds entity/particle layers here.
 */
export class Renderer {
  readonly canvas: HTMLCanvasElement;
  readonly ctx: CanvasRenderingContext2D;
  readonly cam = new Camera();
  terrain: TerrainLayer | null = null;

  private raf = 0;
  private lastT = 0;
  private dpr = 1;
  private shakeX = 0;
  private shakeY = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('2d context unavailable');
    this.ctx = ctx;
    window.addEventListener('resize', () => this.resize());
    // Phones report the wrong height while the URL bar slides away: it sits
    // between innerHeight (too tall) and innerWidth-free layout, so the
    // canvas would sit under the browser chrome or leave a gap. visualViewport
    // tracks the visible bit exactly — resize on it too.
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', () => this.resize());
    }
    window.addEventListener('orientationchange', () => this.resize());
    document.addEventListener('fullscreenchange', () => this.resize());
    this.resize();
  }

  get cssW(): number {
    return this.canvas.clientWidth || window.innerWidth;
  }

  get cssH(): number {
    return this.canvas.clientHeight || window.innerHeight;
  }

  resize(): void {
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    const vp = window.visualViewport;
    const w = Math.floor(vp ? vp.width : window.innerWidth);
    const h = Math.floor(vp ? vp.height : window.innerHeight);
    this.canvas.width = Math.floor(w * this.dpr);
    this.canvas.height = Math.floor(h * this.dpr);
    this.canvas.style.width = `${w}px`;
    this.canvas.style.height = `${h}px`;
    this.cam.setView(w, h);
  }

  /** Begin a frame: clear + draw sky (screen space). Returns nothing; call applyWorld next. */
  beginFrame(themeId: ThemeId, shakeX = 0, shakeY = 0): void {
    this.shakeX = shakeX;
    this.shakeY = shakeY;
    const { ctx, dpr } = this;
    const w = this.cssW;
    const h = this.cssH;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const theme = THEMES[themeId];
    const g = ctx.createLinearGradient(0, 0, 0, h);
    g.addColorStop(0, theme.skyTop);
    g.addColorStop(1, theme.skyBottom);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);

    // Per-theme decor: sun/stars/clouds/skyline/shapes/… + the two parallax
    // hill bands, all screen space (backdrop.ts owns the layer list).
    drawBackdrop(ctx, themeId, this.cam.x, w, h, performance.now() / 1000);

    ctx.save();
  }

  /** Switch to world space (camera transform applied). */
  applyWorld(): void {
    const { ctx, dpr, cam } = this;
    ctx.setTransform(
      dpr * cam.zoom,
      0,
      0,
      dpr * cam.zoom,
      dpr * (this.cssW / 2 - cam.x * cam.zoom) + this.shakeX * dpr,
      dpr * (this.cssH / 2 - cam.y * cam.zoom) + this.shakeY * dpr,
    );
  }

  endFrame(): void {
    this.ctx.restore();
  }

  drawTerrain(): void {
    if (this.terrain) this.ctx.drawImage(this.terrain.canvas, 0, 0);
  }

  /** World-space helpers for debug overlays. */
  strokeWorldCircle(x: number, y: number, r: number, color: string, width = 3): void {
    const { ctx } = this;
    ctx.strokeStyle = color;
    ctx.lineWidth = width;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.stroke();
  }

  startLoop(cb: (dt: number, now: number) => void): void {
    const step = (now: number) => {
      const dt = this.lastT ? Math.min(0.1, (now - this.lastT) / 1000) : 1 / 60;
      this.lastT = now;
      cb(dt, now);
      this.raf = requestAnimationFrame(step);
    };
    this.raf = requestAnimationFrame(step);
  }

  stopLoop(): void {
    cancelAnimationFrame(this.raf);
  }

  worldBoundsText(): string {
    return `${WORLD_W}×${WORLD_H}`;
  }
}
